/**
 * harnessRouting.ts — Feature flag routing logic for Orca Harness v2
 *
 * Determines which version (v1 legacy /chat or v2 /session/:id) a user should see
 * based on their `harness_v2_enabled` profile flag.
 *
 * Phase: 47-01
 */

import { supabase } from './supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export type RoutingDestination = '/chat' | '/chat-legacy' | '/session/new' | '/session/:id';

export type HarnessRoutingResult = {
  destination: RoutingDestination;
  reason: 'flag_off' | 'flag_on' | 'no_session' | 'error';
  sessionId?: string;
};

// ── Module-level cache (30-second TTL) ───────────────────────────────────────

const cache = new Map<string, { value: boolean; timestamp: number }>();
const CACHE_TTL_MS = 30_000;

function getCachedV2(userId: string): boolean | null {
  const entry = cache.get(userId);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
    return entry.value;
  }
  cache.delete(userId);
  return null;
}

function setCachedV2(userId: string, value: boolean): void {
  cache.set(userId, { value, timestamp: Date.now() });
}

function clearCacheForUser(userId: string): void {
  cache.delete(userId);
}

// ── Core routing functions ────────────────────────────────────────────────────

/**
 * Check if a user should use v2 (Orca Harness v2 workspace).
 * Uses profile's harness_v2_enabled flag.
 *
 * C1/H1 fix: profiles table PK is `id` (not `user_id`) — queries use .eq('id', userId)
 */
export async function shouldUseV2(userId: string): Promise<boolean> {
  if (!userId || userId === 'anon') return false;

  // Check cache first
  const cached = getCachedV2(userId);
  if (cached !== null) return cached;

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('harness_v2_enabled')
      .eq('id', userId)  // C1/H1 fix: PK is 'id', not 'user_id'
      .single();

    if (error || !data) {
      // If column doesn't exist (42703/PGRST204) or profile row missing, default to v2
      const code = (error as any)?.code ?? '';
      const isMissingColumn = code === '42703' || code === 'PGRST204';
      console.warn('[harnessRouting] shouldUseV2: profile not found or error, defaulting to', isMissingColumn ? 'v2 (missing column)' : 'v2', error);
      return true; // default to v2 — v1 is legacy
    }

    const enabled = data.harness_v2_enabled === true;
    setCachedV2(userId, enabled);
    return enabled;
  } catch (err) {
    console.error('[harnessRouting] shouldUseV2: unexpected error', err);
    return false;
  }
}

/**
 * Get the routing destination for a user based on their v2 flag and session state.
 *
 * Flag ON + no session → /session/new (v2 entry point)
 * Flag ON + has session → /session/:id (continue existing session)
 * Flag OFF or error → /chat (v1 legacy)
 */
export async function getRoutingDestination(
  userId: string,
  currentSessionId?: string
): Promise<HarnessRoutingResult> {
  try {
    const useV2 = await shouldUseV2(userId);

    if (useV2) {
      // Default to /app (CoworkShell) — user creates workspace there, then starts session.
      // /session/new requires existing workspace_id which new users don't have yet.
      return { destination: '/app', reason: 'flag_on' };
    }

    return { destination: '/chat', reason: 'flag_off' };
  } catch (err) {
    console.error('[harnessRouting] getRoutingDestination: error', err);
    return { destination: '/chat', reason: 'error' };
  }
}

/**
 * Toggle the harness_v2_enabled flag for a user.
 * Invalidates the cache for that userId.
 *
 * C1/H1 fix: profiles table PK is `id` (not `user_id`) — queries use .eq('id', userId)
 */
export async function toggleHarnessV2(
  userId: string,
  enabled: boolean
): Promise<{ success: boolean; error?: string }> {
  if (!userId || userId === 'anon') {
    return { success: false, error: 'Invalid userId' };
  }

  try {
    const { error } = await supabase
      .from('profiles')
      .update({ harness_v2_enabled: enabled })
      .eq('id', userId);  // C1/H1 fix: PK is 'id', not 'user_id'

    if (error) {
      console.error('[harnessRouting] toggleHarnessV2: update failed', error);
      return { success: false, error: error.message };
    }

    // Invalidate cache so next shouldUseV2 call fetches fresh data
    clearCacheForUser(userId);
    return { success: true };
  } catch (err) {
    console.error('[harnessRouting] toggleHarnessV2: unexpected error', err);
    return { success: false, error: 'Unexpected error updating preference' };
  }
}

/**
 * Get current harness v2 status for a user.
 */
export async function getHarnessV2Status(
  userId: string
): Promise<{ enabled: boolean; lastToggled?: string }> {
  if (!userId || userId === 'anon') {
    return { enabled: false };
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('harness_v2_enabled, updated_at')
      .eq('id', userId)  // C1/H1 fix: PK is 'id', not 'user_id'
      .single();

    if (error || !data) {
      return { enabled: false };
    }

    return {
      enabled: data.harness_v2_enabled === true,
      lastToggled: data.updated_at ?? undefined,
    };
  } catch (err) {
    console.error('[harnessRouting] getHarnessV2Status: error', err);
    return { enabled: false };
  }
}
